import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/shared/contexts/AuthContext';
import { toast } from 'sonner';
import {
  ArrowLeft, ClipboardList, CheckCircle, Package, MapPin, X,
  ScanLine, ChevronRight, Clock, AlertTriangle, Play, Pause,
  QrCode, Navigation, ChevronDown, ChevronUp, Warehouse as WarehouseIcon,
  ShoppingCart, Box, CheckSquare, RotateCcw, Plus, Minus, Wifi, WifiOff
} from 'lucide-react';
import {
  WarehouseCard, WarehouseButton, WarehouseBadge,
  WarehouseProgress, WarehouseScanResult
} from '../components/WarehouseUIKit';
import { useWarehouseTheme } from '../context/WarehouseThemeContext';
import {
  isNative, HapticService, OfflineStorage, NetworkService
} from '@/services/nativeServices';
import '../styles/warehouse.css';

import { pickingAPI } from '@/shared/services/api';

export const WarehousePicking = () => {
  const navigate = useNavigate();
  const { isDark } = useWarehouseTheme();
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(true);
  const [pickingTasks, setPickingTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [scanInput, setScanInput] = useState('');
  const [recentScans, setRecentScans] = useState([]);
  const [pickQuantity, setPickQuantity] = useState(0);
  const [showPickModal, setShowPickModal] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const inputRef = useRef(null);

  // Fetch picking tasks from API
  const fetchPickingTasks = async () => {
    setLoading(true);
    try {
      const data = await pickingAPI.getOrders();
      setPickingTasks(data);
    } catch (err) {
      console.error('Error fetching picking tasks:', err);
      toast.error('Failed to load picking tasks');
    }
    setLoading(false);
  };

  // Sync offline queue when back online
  const syncOfflineQueue = async () => {
    if (!isNative() || isSyncing) return;
    setIsSyncing(true);
    const queue = await OfflineStorage.getQueue();
    if (queue.length > 0) {
      let successCount = 0;
      for (const action of queue) {
        try {
          if (action.type === 'confirm_pick') {
            await pickingAPI.confirmPick(action.data);
            successCount++;
          }
        } catch (err) {
          console.error('Failed to sync action:', err);
        }
      }
      await OfflineStorage.clearQueue();
      if (successCount > 0) {
        toast.success(`Synced ${successCount} offline actions`);
        fetchPickingTasks();
      }
    }
    setIsSyncing(false);
  };

  // Initialize network status and native services
  useEffect(() => {
    const initNetwork = async () => {
      if (isNative()) {
        await NetworkService.initialize();
        setIsOnline(NetworkService.getStatus());
        NetworkService.onStatusChange((connected) => {
          setIsOnline(connected);
          if (connected) {
            toast.success('Back online - syncing data...');
            syncOfflineQueue();
          } else {
            toast.warning('You are offline - changes will sync when connected');
          }
        });
      }
    };
    initNetwork();
    fetchPickingTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedTask && inputRef.current) {
      inputRef.current.focus();
    }
  }, [selectedTask, currentItemIndex]);

  const filteredTasks = pickingTasks.filter(task => {
    if (activeTab === 'all') return true;
    if (activeTab === 'pending') return task.status === 'pending' || task.status === 'ready_to_pick';
    return task.status === activeTab;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': case 'picking': return 'info';
      case 'pending': case 'ready_to_pick': return 'warning';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed': return 'COMPLETED';
      case 'in_progress': case 'picking': return 'IN PROGRESS';
      case 'pending': case 'ready_to_pick': return 'PENDING';
      default: return status?.toUpperCase() || 'UNKNOWN';
    }
  };

  const handleStartPicking = async (task) => {
    try {
      const data = await pickingAPI.startPicking(task.order_id);

      // Transform pick_list to items format
      const taskWithItems = {
        ...task,
        status: 'in_progress',
        items: data.pick_list?.map(item => ({
          sku: item.sku,
          product_name: item.product_name || item.sku,
          location: item.location,
          qty: item.quantity || item.qty_to_pick,
          picked: item.picked || item.already_picked || 0
        })) || task.items || []
      };
      setSelectedTask(taskWithItems);
      setCurrentItemIndex(0);
      toast.success(`Started picking ${task.order_id}`);
    } catch (err) {
      console.error('Error starting pick:', err);
      toast.error(err.message || 'Failed to start picking');
      // Fallback
      if (!isOnline) {
        setSelectedTask({
          ...task,
          status: 'in_progress',
          items: task.items || []
        });
        setCurrentItemIndex(0);
      }
    }
  };

  const currentItem = selectedTask?.items?.[currentItemIndex];

  const handleScan = async (barcode) => {
    if (!barcode.trim() || !currentItem) return;

    const result = {
      barcode,
      timestamp: new Date().toISOString()
    };

    // Check if barcode matches current item
    if (barcode === currentItem.sku || barcode === currentItem.gtin || barcode === currentItem.location) {
      result.status = 'OK';
      result.message = `Verified: ${currentItem.product_name || currentItem.sku}`;

      // Show pick quantity modal
      setPickQuantity(currentItem.qty - (currentItem.picked || 0));
      setShowPickModal(true);
    } else {
      result.status = 'error';
      result.message = `Expected: ${currentItem.sku} or ${currentItem.location}`;
    }

    setRecentScans([result, ...recentScans.slice(0, 4)]);
    setScanInput('');
  };

  const handleConfirmPick = async () => {
    if (!selectedTask || !currentItem) return;

    // Haptic feedback on native
    if (isNative()) {
      await HapticService.impact('medium');
    }

    const pickData = {
      order_id: selectedTask.order_id,
      sku: currentItem.sku,
      quantity: pickQuantity,
      location: currentItem.location
    };

    // If offline, queue for later sync
    if (!isOnline && isNative()) {
      await OfflineStorage.queueAction({
        type: 'confirm_pick',
        data: pickData,
        timestamp: new Date().toISOString()
      });
      toast.info(`Queued for sync: ${pickQuantity}x ${currentItem.sku}`);
    } else {
      try {
        await fetch(`${API_URL}/api/picking/confirm`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(pickData)
        });
      } catch (err) {
        console.error('Error confirming pick:', err);
        if (isNative()) {
          await OfflineStorage.queueAction({
            type: 'confirm_pick',
            data: pickData,
            timestamp: new Date().toISOString()
          });
          toast.warning('Saved offline - will sync when connected');
        }
      }
    }

    // Update local state
    const updatedItems = selectedTask.items.map((item, idx) =>
      idx === currentItemIndex
        ? { ...item, picked: (item.picked || 0) + pickQuantity }
        : item
    );

    const updatedTask = { ...selectedTask, items: updatedItems };
    setSelectedTask(updatedTask);

    // Add success scan
    setRecentScans([{
      barcode: currentItem.sku,
      status: 'OK',
      message: `Picked ${pickQuantity}x ${currentItem.product_name || currentItem.sku}`,
      timestamp: new Date().toISOString()
    }, ...recentScans.slice(0, 4)]);

    // Success haptic
    if (isNative()) {
      await HapticService.notification('success');
    }

    toast.success(`Picked ${pickQuantity}x ${currentItem.sku}`);
    setShowPickModal(false);

    // Move to next item if current is complete
    if ((currentItem.picked || 0) + pickQuantity >= currentItem.qty) {
      if (currentItemIndex < selectedTask.items.length - 1) {
        setCurrentItemIndex(currentItemIndex + 1);
        toast.info('Moving to next item...');
      } else {
        toast.success('All items picked! Order complete.');
      }
    }
  };

  const handleShortPick = () => {
    toast.warning(`Short pick recorded for ${currentItem.sku}`);
    setShowPickModal(false);
    // Move to next item
    if (currentItemIndex < selectedTask.items.length - 1) {
      setCurrentItemIndex(currentItemIndex + 1);
    }
  };

  const totalStats = {
    pending: pickingTasks.filter(t => t.status === 'pending' || t.status === 'ready_to_pick').length,
    inProgress: pickingTasks.filter(t => t.status === 'in_progress' || t.status === 'picking').length,
    completed: pickingTasks.filter(t => t.status === 'completed').length
  };

  const totalPicked = selectedTask?.items?.reduce((acc, i) => acc + (i.picked || 0), 0) || 0;
  const totalToPick = selectedTask?.items?.reduce((acc, i) => acc + i.qty, 0) || 0;

  return (
    <div className="warehouse-app min-h-screen pb-20" data-testid="warehouse-picking">
      {/* Header */}
      <header
        className="wh-safe-top px-4 py-4 flex items-center gap-4"
        style={{
          background: isDark ? 'var(--wh-bg-secondary)' : '#ea580c',
          color: 'white'
        }}
      >
        <button
          onClick={() => selectedTask ? setSelectedTask(null) : navigate('/mobile')}
          className="p-2 -ml-2 rounded-full"
          style={{ background: 'rgba(255,255,255,0.1)' }}
          data-testid="picking-back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold">
            {selectedTask ? `Picking: ${selectedTask.order_id}` : 'Picking'}
          </h1>
          <p className="text-sm opacity-80">
            {selectedTask ? selectedTask.customer : 'Pick items for orders'}
          </p>
        </div>
        {selectedTask ? (
          <div className="text-right">
            <p className="text-2xl font-bold">{totalPicked}/{totalToPick}</p>
            <p className="text-xs opacity-80">Items Picked</p>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            {!isOnline && (
              <div className="p-2 rounded-full bg-yellow-500">
                <WifiOff className="w-4 h-4 text-white" />
              </div>
            )}
            <div className="text-right">
              <p className="text-2xl font-bold">{totalStats.pending}</p>
              <p className="text-xs opacity-80">Pending</p>
            </div>
          </div>
        )}
      </header>

      {/* Pick Quantity Modal */}
      {showPickModal && currentItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="wh-card w-full max-w-md wh-animate-slide-up" style={{ background: 'var(--wh-bg-primary)' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold" style={{ color: 'var(--wh-text-primary)' }}>
                Confirm Pick
              </h3>
              <button
                onClick={() => setShowPickModal(false)}
                className="p-2 rounded-full"
                style={{ background: 'var(--wh-bg-tertiary)' }}
              >
                <X className="w-5 h-5" style={{ color: 'var(--wh-text-secondary)' }} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-4 p-3 rounded-xl" style={{ background: 'var(--wh-bg-secondary)' }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: '#ea580c' }}>
                  <Package className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-bold" style={{ color: 'var(--wh-text-primary)' }}>
                    {currentItem.product_name || currentItem.sku}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <MapPin className="w-3 h-3" style={{ color: '#ea580c' }} />
                    <span className="font-mono font-bold" style={{ color: '#ea580c' }}>
                      {currentItem.location}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-xl text-center" style={{ background: 'var(--wh-warning-muted)' }}>
                  <p className="text-2xl font-bold" style={{ color: 'var(--wh-warning)' }}>
                    {currentItem.qty}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--wh-text-secondary)' }}>Required</p>
                </div>
                <div className="p-3 rounded-xl text-center" style={{ background: 'var(--wh-success-muted)' }}>
                  <p className="text-2xl font-bold" style={{ color: 'var(--wh-success)' }}>
                    {currentItem.picked || 0}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--wh-text-secondary)' }}>Picked</p>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block" style={{ color: 'var(--wh-text-secondary)' }}>
                  Quantity to Pick
                </label>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setPickQuantity(Math.max(1, pickQuantity - 1))}
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ background: 'var(--wh-bg-tertiary)' }}
                  >
                    <Minus className="w-5 h-5" style={{ color: 'var(--wh-text-primary)' }} />
                  </button>
                  <input
                    type="number"
                    value={pickQuantity}
                    onChange={(e) => setPickQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    className="flex-1 h-12 text-center text-2xl font-bold rounded-xl border"
                    style={{
                      background: 'var(--wh-bg-tertiary)',
                      borderColor: 'var(--wh-border)',
                      color: 'var(--wh-text-primary)'
                    }}
                  />
                  <button
                    onClick={() => setPickQuantity(pickQuantity + 1)}
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ background: 'var(--wh-bg-tertiary)' }}
                  >
                    <Plus className="w-5 h-5" style={{ color: 'var(--wh-text-primary)' }} />
                  </button>
                </div>
              </div>

              <div className="flex gap-2">
                <WarehouseButton
                  variant="secondary"
                  className="flex-1"
                  onClick={handleShortPick}
                >
                  Short Pick
                </WarehouseButton>
                <WarehouseButton
                  variant="success"
                  className="flex-1"
                  icon={CheckCircle}
                  onClick={handleConfirmPick}
                  data-testid="confirm-pick-btn"
                >
                  Confirm ({pickQuantity})
                </WarehouseButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Task Detail View */}
      {selectedTask ? (
        <div className="px-4 mt-4 wh-animate-slide-up">
          {/* Progress */}
          <WarehouseCard elevated className="mb-4">
            <div className="flex items-center justify-between mb-3">
              <span className="wh-text-caption">Overall Progress</span>
              <span className="font-bold" style={{ color: 'var(--wh-success)' }}>
                {totalPicked}/{totalToPick} items
              </span>
            </div>
            <WarehouseProgress
              value={totalPicked}
              max={totalToPick}
              showPercentage
            />
          </WarehouseCard>

          {/* Current Item to Pick */}
          {currentItem && (currentItem.picked || 0) < currentItem.qty && (
            <div className="mb-4">
              <h3 className="wh-text-caption font-semibold mb-2 uppercase flex items-center gap-2">
                <Navigation className="w-4 h-4" style={{ color: '#ea580c' }} />
                Go to Location
              </h3>
              <WarehouseCard
                elevated
                status="warning"
                className="border-2"
                style={{ borderColor: '#ea580c' }}
              >
                <div className="text-center mb-4">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl" style={{ background: '#ea580c' }}>
                    <MapPin className="w-5 h-5 text-white" />
                    <span className="text-2xl font-mono font-bold text-white">
                      {currentItem.location}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-3 rounded-xl mb-4" style={{ background: 'var(--wh-bg-secondary)' }}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'var(--wh-warning-muted)' }}>
                    <Package className="w-6 h-6" style={{ color: 'var(--wh-warning)' }} />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold" style={{ color: 'var(--wh-text-primary)' }}>
                      {currentItem.product_name || currentItem.sku}
                    </p>
                    <p className="text-sm font-mono" style={{ color: 'var(--wh-text-secondary)' }}>
                      SKU: {currentItem.sku}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold" style={{ color: 'var(--wh-warning)' }}>
                      {currentItem.qty - (currentItem.picked || 0)}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--wh-text-tertiary)' }}>to pick</p>
                  </div>
                </div>

                {/* Scanner Input */}
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <QrCode className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: 'var(--wh-text-tertiary)' }} />
                    <input
                      ref={inputRef}
                      type="text"
                      value={scanInput}
                      onChange={(e) => setScanInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleScan(scanInput)}
                      placeholder="Scan item or location..."
                      className="w-full h-12 pl-10 pr-4 rounded-xl border"
                      style={{
                        background: 'var(--wh-bg-tertiary)',
                        borderColor: 'var(--wh-border)',
                        color: 'var(--wh-text-primary)'
                      }}
                      autoFocus
                      data-testid="pick-scan-input"
                    />
                  </div>
                  <WarehouseButton
                    variant="primary"
                    onClick={() => handleScan(scanInput)}
                    style={{ background: '#ea580c' }}
                  >
                    <ScanLine className="w-5 h-5" />
                  </WarehouseButton>
                </div>
              </WarehouseCard>
            </div>
          )}

          {/* Items List */}
          <h3 className="wh-text-caption font-semibold mb-2 uppercase">Pick List ({selectedTask.items?.length || 0} items)</h3>
          {selectedTask.items?.map((item, i) => {
            const isComplete = (item.picked || 0) >= item.qty;
            const isCurrent = i === currentItemIndex && !isComplete;

            return (
              <WarehouseCard
                key={i}
                status={isComplete ? 'success' : isCurrent ? 'warning' : 'default'}
                className={`mb-2 ${isCurrent ? 'ring-2' : ''}`}
                style={isCurrent ? { ringColor: '#ea580c' } : {}}
                interactive={!isComplete}
                onClick={() => !isComplete && setCurrentItemIndex(i)}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{
                      background: isComplete ? 'var(--wh-success-muted)' : isCurrent ? '#ea580c' : 'var(--wh-bg-tertiary)'
                    }}
                  >
                    {isComplete ? (
                      <CheckCircle className="w-5 h-5" style={{ color: 'var(--wh-success)' }} />
                    ) : (
                      <span className="text-sm font-bold" style={{ color: isCurrent ? 'white' : 'var(--wh-text-secondary)' }}>
                        {i + 1}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate" style={{ color: 'var(--wh-text-primary)' }}>
                      {item.product_name || item.sku}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <MapPin className="w-3 h-3" style={{ color: '#ea580c' }} />
                      <span className="text-xs font-mono font-bold" style={{ color: '#ea580c' }}>
                        {item.location}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold" style={{ color: isComplete ? 'var(--wh-success)' : 'var(--wh-text-primary)' }}>
                      {item.picked || 0}/{item.qty}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--wh-text-tertiary)' }}>picked</p>
                  </div>
                </div>
              </WarehouseCard>
            );
          })}

          {/* Recent Scans */}
          {recentScans.length > 0 && (
            <div className="mt-4">
              <h3 className="wh-text-caption font-semibold mb-2 uppercase">Recent Scans</h3>
              {recentScans.map((scan, i) => (
                <WarehouseScanResult key={i} {...scan} />
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Tabs */}
          <div
            className="px-4 py-3 flex gap-2 overflow-x-auto"
            style={{ background: 'var(--wh-bg-secondary)' }}
          >
            {[
              { id: 'all', label: 'All' },
              { id: 'pending', label: 'Pending' },
              { id: 'in_progress', label: 'In Progress' },
              { id: 'completed', label: 'Completed' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors"
                style={{
                  background: activeTab === tab.id ? '#ea580c' : 'var(--wh-bg-tertiary)',
                  color: activeTab === tab.id ? 'white' : 'var(--wh-text-secondary)'
                }}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Summary Stats */}
          <div className="px-4 mt-4">
            <div className="grid grid-cols-3 gap-3">
              <div
                className="text-center p-3 rounded-xl"
                style={{ background: 'var(--wh-warning-muted)' }}
              >
                <p className="text-xl font-bold" style={{ color: 'var(--wh-warning)' }}>
                  {totalStats.pending}
                </p>
                <p className="text-xs" style={{ color: 'var(--wh-text-secondary)' }}>Pending</p>
              </div>
              <div
                className="text-center p-3 rounded-xl"
                style={{ background: 'var(--wh-info-muted)' }}
              >
                <p className="text-xl font-bold" style={{ color: 'var(--wh-info)' }}>
                  {totalStats.inProgress}
                </p>
                <p className="text-xs" style={{ color: 'var(--wh-text-secondary)' }}>Active</p>
              </div>
              <div
                className="text-center p-3 rounded-xl"
                style={{ background: 'var(--wh-success-muted)' }}
              >
                <p className="text-xl font-bold" style={{ color: 'var(--wh-success)' }}>
                  {totalStats.completed}
                </p>
                <p className="text-xs" style={{ color: 'var(--wh-text-secondary)' }}>Done</p>
              </div>
            </div>
          </div>

          {/* Task List */}
          <div className="px-4 mt-4 space-y-3">
            {loading ? (
              <div className="text-center py-8">
                <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto" style={{ borderColor: '#ea580c', borderTopColor: 'transparent' }} />
                <p className="mt-2 text-sm" style={{ color: 'var(--wh-text-secondary)' }}>Loading tasks...</p>
              </div>
            ) : filteredTasks.length === 0 ? (
              <div className="text-center py-8">
                <ClipboardList className="w-12 h-12 mx-auto mb-2" style={{ color: 'var(--wh-text-tertiary)' }} />
                <p style={{ color: 'var(--wh-text-secondary)' }}>No picking tasks found</p>
              </div>
            ) : (
              filteredTasks.map(task => (
                <WarehouseCard
                  key={task.order_id}
                  interactive
                  status={getStatusColor(task.status)}
                  onClick={() => handleStartPicking(task)}
                  data-testid={`task-${task.order_id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold" style={{ color: 'var(--wh-text-primary)' }}>
                          {task.order_id}
                        </h3>
                        {task.priority === 'high' && (
                          <WarehouseBadge variant="error">PRIORITY</WarehouseBadge>
                        )}
                      </div>
                      <p className="text-sm" style={{ color: 'var(--wh-text-secondary)' }}>
                        {task.customer}
                      </p>
                    </div>
                    <WarehouseBadge variant={getStatusColor(task.status)}>
                      {getStatusLabel(task.status)}
                    </WarehouseBadge>
                  </div>

                  <WarehouseProgress
                    value={task.picked_quantity || 0}
                    max={task.total_quantity || 1}
                    label={`${task.picked_quantity || 0}/${task.total_quantity || 0} items`}
                  />

                  <div className="flex items-center justify-between mt-3 pt-3" style={{ borderTop: '1px solid var(--wh-divider)' }}>
                    <div className="flex items-center gap-1 text-xs" style={{ color: 'var(--wh-text-secondary)' }}>
                      <Clock className="w-3 h-3" />
                      <span>Due: {task.due_date ? new Date(task.due_date).toLocaleDateString() : 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs" style={{ color: 'var(--wh-text-secondary)' }}>
                      <MapPin className="w-3 h-3" />
                      <span>{task.locations_to_visit || 0} locations</span>
                    </div>
                    {task.status !== 'completed' && (
                      <WarehouseButton
                        variant="primary"
                        size="small"
                        icon={Play}
                        style={{ background: '#ea580c' }}
                        data-testid={`start-${task.order_id}`}
                      >
                        Start
                      </WarehouseButton>
                    )}
                  </div>
                </WarehouseCard>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default WarehousePicking;
